import JSZip from 'jszip';
const FORM_JSON_RENDER_KEY_PREFIX = '_render:';
/**
 * Parses form.json to extract the pdfContentUrl value and signatureFields array.
 * The structure is: `{ "_render:<template>": { pdfContentUrl: { _value: "..." }, signatureFields: [...] } }`
 */
function parseFormJson(formJsonContent) {
    const parsed = JSON.parse(formJsonContent);
    const renderKey = Object.keys(parsed).find((k) => k.startsWith(FORM_JSON_RENDER_KEY_PREFIX));
    if (!renderKey) {
        throw new Error('Consent bundle form.json missing _render key');
    }
    const renderData = parsed[renderKey];
    const pdfContentUrl = renderData?.pdfContentUrl;
    const pdfRelativeUrl = typeof pdfContentUrl === 'string' ? pdfContentUrl : (pdfContentUrl?._value ?? '');
    if (!pdfRelativeUrl) {
        throw new Error('Consent bundle form.json missing pdfContentUrl');
    }
    const signatureFields = Array.isArray(renderData?.signatureFields)
        ? renderData.signatureFields
        : [];
    return { pdfRelativeUrl, signatureFields };
}
/**
 * Extracts the original PDF bytes and signature field geometry from a consent
 * bundle zip, then merges the field positions with the collected signature data
 * to produce overlay fields ready for PDF generation.
 */
export async function extractBundlePdfData(bundleUrl, signedSignatures) {
    const response = await fetch(bundleUrl);
    if (!response.ok) {
        throw new Error(`Failed to fetch consent bundle: ${response.status}`);
    }
    const buffer = await response.arrayBuffer();
    const zip = await JSZip.loadAsync(new Uint8Array(buffer));
    const formJsonFile = zip.file('form.json');
    if (!formJsonFile) {
        throw new Error('Consent bundle does not contain form.json');
    }
    const formJsonContent = await formJsonFile.async('string');
    const { pdfRelativeUrl, signatureFields } = parseFormJson(formJsonContent);
    const pdfPath = pdfRelativeUrl.replace(/\?.*$/, '').replace(/^\.\//, '');
    const pdfFile = zip.file(pdfPath);
    if (!pdfFile) {
        throw new Error(`Consent bundle does not contain PDF file: ${pdfPath}`);
    }
    const pdfBytes = await pdfFile.async('uint8array');
    const signedByKey = new Map();
    for (const summary of signedSignatures) {
        signedByKey.set(summary.ec__key, summary);
    }
    const signedOverlays = signatureFields
        .filter((field) => field.ec__key && signedByKey.has(field.ec__key))
        .map((field) => {
        const summary = signedByKey.get(field.ec__key);
        return {
            ec__key: field.ec__key,
            name: summary.name,
            id: summary.id,
            date: summary.date,
            page: field.page,
            xPct: field.xPct,
            yPct: field.yPct,
            widthPct: field.widthPct,
            heightPct: field.heightPct,
        };
    });
    return { pdfBytes, signedOverlays };
}
